import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { calculateConsumption, extrapolateToYear } from './energyLogic';
import { LOGO_URL, SITE_NAME, APP_NAME, COLORS, AD_CONFIG } from './constants';

const loadImageAsDataURL = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading logo:', error);
    return null;
  }
};

const createSlug = (text) => {
  if (!text || text.trim() === '') return 'Privat';
  
  return text
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/Ä/g, 'Ae')
    .replace(/Ö/g, 'Oe')
    .replace(/Ü/g, 'Ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-zA-Z0-9.]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-+/g, '-');
};

const formatValue = (value, type) => {
  if (type === 'electricity') {
    return value.toFixed(1);
  }
  if (type === 'gas' || type === 'water' || type === 'waterCold' || type === 'waterWarm') {
    return value.toFixed(3);
  }
  return value.toFixed(2);
};

export const generatePDF = async (data, activeObject, activeMeter) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  // Load logo with proper aspect ratio
  const logoDataURL = await loadImageAsDataURL(LOGO_URL);
  
  // Header with logo (maintain aspect ratio - no distortion)
  if (logoDataURL) {
    const imgProps = doc.getImageProperties(logoDataURL);
    const aspectRatio = imgProps.width / imgProps.height;
    const logoHeight = 10;
    const logoWidth = logoHeight * aspectRatio;
    doc.addImage(logoDataURL, 'PNG', 14, 10, logoWidth, logoHeight, undefined, 'FAST');
  }
  
  doc.setFontSize(20);
  doc.setTextColor(0, 0, 102);
  doc.text('Mein Energie-Check', 50, 18);
  
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(SITE_NAME, 50, 24);
  
  // Date
  doc.setFontSize(9);
  doc.text(`Erstellt am: ${format(new Date(), 'dd.MM.yyyy HH:mm', { locale: de })}`, 14, 35);
  
  // Company Name (prominent)
  if (data.companyName) {
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.setFont(undefined, 'bold');
    doc.text(`Name/Unternehmen: ${data.companyName}`, 14, 43);
    doc.setFont(undefined, 'normal');
  }
  
  let currentY = data.companyName ? 52 : 45;
  
  // Object & Meter Info
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text(`Objekt: ${activeObject.name}`, 14, currentY);
  doc.text(`Zähler: ${activeMeter.name} (${getMeterTypeLabel(activeMeter.type)})`, 14, currentY + 7);
  
  currentY += 15;
  
  // Readings Table
  if (activeMeter.readings && activeMeter.readings.length > 0) {
    const sortedReadings = [...activeMeter.readings].sort((a, b) => 
      new Date(a.timestamp) - new Date(b.timestamp)
    );
    
    const tableData = sortedReadings.map((reading, index) => {
      const consumption = index > 0 ? calculateConsumption(
        sortedReadings[index - 1],
        reading,
        activeMeter
      ) : null;
      
      const forecast = consumption !== null && index > 0 ? (() => {
        const timeDiff = new Date(reading.timestamp) - new Date(sortedReadings[index - 1].timestamp);
        const hours = timeDiff / (1000 * 60 * 60);
        const days = hours / 24;
        return days > 0 ? extrapolateToYear(consumption, days) : null;
      })() : null;
      
      return [
        format(new Date(reading.timestamp), 'dd.MM.yyyy HH:mm', { locale: de }),
        formatValue(reading.value, activeMeter.type),
        activeMeter.type === 'gas' || activeMeter.type === 'water' || activeMeter.type === 'waterCold' || activeMeter.type === 'waterWarm' ? 'm³' : 'kWh',
        consumption !== null ? consumption.toFixed(2) : '—',
        consumption !== null ? 'kWh' : '—',
        forecast !== null && forecast > 0 ? forecast.toFixed(0) : '—'
      ];
    });
    
    doc.autoTable({
      startY: currentY,
      head: [['Datum/Zeit', 'Stand', 'Einheit', 'Verbrauch', 'Einheit', '365-Tage (kWh)']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [0, 0, 102] },
      styles: { fontSize: 8 }
    });
    
    // 365-Day Forecast Analysis (default: last 2 readings)
    if (sortedReadings.length >= 2) {
      const lastTwo = sortedReadings.slice(-2);
      const consumption = calculateConsumption(lastTwo[0], lastTwo[1], activeMeter);
      const timeDiff = new Date(lastTwo[1].timestamp) - new Date(lastTwo[0].timestamp);
      const hours = timeDiff / (1000 * 60 * 60);
      const days = hours / 24;
      
      if (days > 0 && consumption !== null) {
        const yearlyForecast = extrapolateToYear(consumption, days);
        
        const finalY = doc.lastAutoTable.finalY + 10;
        doc.setFontSize(12);
        doc.setTextColor(255, 126, 0);
        doc.text('365-Tage-Prognose (basierend auf letzten 2 Ablesungen):', 14, finalY);
        doc.setFontSize(16);
        doc.setTextColor(0, 0, 0);
        doc.text(`${yearlyForecast.toFixed(2)} kWh/Jahr`, 14, finalY + 8);
        
        doc.setFontSize(8);
        doc.setTextColor(100, 100, 100);
        doc.text(`Zeitraum: ${days.toFixed(1)} Tage`, 14, finalY + 14);
        
        currentY = finalY + 20;
      } else {
        currentY = doc.lastAutoTable.finalY + 10;
      }
    } else {
      currentY = doc.lastAutoTable.finalY + 10;
    }
  }
  
  // Gas-specific info
  if (activeMeter.type === 'gas') {
    const latestReading = activeMeter.readings && activeMeter.readings.length > 0
      ? [...activeMeter.readings].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))[0]
      : null;
    
    const gasZ = latestReading?.gasParams?.zNumber || activeMeter.params?.zNumber;
    const gasHeat = latestReading?.gasParams?.heatingValue || activeMeter.params?.heatingValue;
    
    doc.setFontSize(10);
    doc.setTextColor(0, 204, 255);
    doc.text('Gas-Umrechnung:', 14, currentY);
    doc.setFontSize(9);
    doc.setTextColor(0, 0, 0);
    doc.text(`Zustandszahl (z-Zahl): ${gasZ ? gasZ.toFixed(4) : '—'}`, 14, currentY + 6);
    doc.text(`Brennwert: ${gasHeat ? gasHeat.toFixed(3) : '—'} kWh/m³`, 14, currentY + 12);
    doc.text(`Formel: kWh = (m³ neu - m³ alt) × Zustandszahl × Brennwert`, 14, currentY + 18);
    currentY += 25;
  }
  
  // Savings Tips
  const tipsY = Math.max(currentY, 150);
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 102);
  doc.text('Spartipps:', 14, tipsY);
  
  doc.setFontSize(9);
  doc.setTextColor(0, 0, 0);
  const tips = [
    '• Standby-Geräte ausschalten spart bis zu 100€/Jahr',
    '• Heiztemperatur um 1°C senken reduziert Kosten um 6%',
    '• LED-Beleuchtung nutzen - 80% weniger Verbrauch'
  ];
  
  tips.forEach((tip, index) => {
    doc.text(tip, 14, tipsY + 8 + (index * 6));
  });
  
  // Footer CTA (clickable link)
  const footerY = doc.internal.pageSize.getHeight() - 20;
  doc.setFillColor(255, 126, 0);
  doc.rect(14, footerY - 5, pageWidth - 28, 15, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(11);
  doc.textWithLink('Jetzt Tarife vergleichen und sparen!', pageWidth / 2, footerY, {
    align: 'center',
    url: AD_CONFIG.link
  });
  doc.setFontSize(9);
  doc.text(SITE_NAME, pageWidth / 2, footerY + 5, { align: 'center' });
  
  // Save with slug
  const timestamp = format(new Date(), 'yyyy-MM-dd-HHmmss');
  const slug = createSlug(data.companyName);
  doc.save(`Energie-Monitor-und-Rechner-${slug}-${timestamp}.pdf`);
};

export const generateCSV = (data) => {
  const rows = [];
  
  // Header with metadata
  rows.push(['Energie-Monitor & Rechner']);
  rows.push([`Quelle: ${SITE_NAME}`]);
  rows.push([`Logo-URL: ${LOGO_URL}`]);
  rows.push([`Name/Unternehmen: ${data.companyName || ''}`]);
  rows.push([`Erstellt am: ${format(new Date(), 'dd.MM.yyyy HH:mm:ss', { locale: de })}`]);
  rows.push([]);
  
  // Column headers
  rows.push([
    'Objekt',
    'Zähler',
    'Typ',
    'Datum/Zeit',
    'Zählerstand',
    'Einheit',
    'Verbrauch (kWh)',
    '365-Tage-Prognose (kWh)',
    'Gas z-Zahl',
    'Gas Brennwert (kWh/m³)'
  ]);
  
  // Data rows
  data.objects.forEach(obj => {
    obj.meters.forEach(meter => {
      if (meter.readings && meter.readings.length > 0) {
        const sortedReadings = [...meter.readings].sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        );
        
        sortedReadings.forEach((reading, index) => {
          const consumption = index > 0 ? calculateConsumption(
            sortedReadings[index - 1],
            reading,
            meter
          ) : null;
          
          const forecast = consumption !== null && index > 0 ? (() => {
            const timeDiff = new Date(reading.timestamp) - new Date(sortedReadings[index - 1].timestamp);
            const hours = timeDiff / (1000 * 60 * 60);
            const days = hours / 24;
            return days > 0 ? extrapolateToYear(consumption, days) : null;
          })() : null;
          
          rows.push([
            obj.name,
            meter.name,
            getMeterTypeLabel(meter.type),
            format(new Date(reading.timestamp), 'dd.MM.yyyy HH:mm:ss', { locale: de }),
            formatValue(reading.value, meter.type),
            meter.type === 'gas' || meter.type === 'water' || meter.type === 'waterCold' || meter.type === 'waterWarm' ? 'm³' : 'kWh',
            consumption !== null ? consumption.toFixed(2) : '',
            forecast !== null ? forecast.toFixed(2) : '',
            meter.type === 'gas' && reading.gasParams ? reading.gasParams.zNumber.toFixed(4) : '',
            meter.type === 'gas' && reading.gasParams ? reading.gasParams.heatingValue.toFixed(3) : ''
          ]);
        });
      }
    });
  });
  
  // Convert to CSV string
  const csvContent = rows.map(row => 
    row.map(cell => `"${cell}"`).join(';')
  ).join('\n');
  
  // Download
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  const timestamp = format(new Date(), 'yyyy-MM-dd-HHmmss');
  const slug = createSlug(data.companyName);
  
  link.setAttribute('href', url);
  link.setAttribute('download', `Energie-Monitor-und-Rechner-${slug}-${timestamp}.csv`);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const importCSV = (file, onSuccess) => {
  const reader = new FileReader();
  
  reader.onload = (e) => {
    try {
      const csv = e.target.result;
      const lines = csv.split('\n');
      
      // Find the header row
      let headerIndex = -1;
      for (let i = 0; i < lines.length; i++) {
        if (lines[i].includes('Objekt') && lines[i].includes('Zähler') && lines[i].includes('Datum/Zeit')) {
          headerIndex = i;
          break;
        }
      }
      
      if (headerIndex === -1) {
        throw new Error('CSV-Header nicht gefunden');
      }
      
      const dataLines = lines.slice(headerIndex + 1).filter(line => line.trim());
      
      const importedData = {
        objects: [],
        companyName: '',
        stats: { totalReadings: 0, analyses: 0, magicDownloads: 0, adClicks: 0, shares: 0 }
      };
      
      // Extract company name from metadata
      for (let i = 0; i < headerIndex; i++) {
        if (lines[i].includes('Name/Unternehmen')) {
          const match = lines[i].match(/Name\/Unternehmen:\s*"([^"]*)"/);
          if (match) {
            importedData.companyName = match[1];
          }
        }
      }
      
      dataLines.forEach(line => {
        const cells = line.split(';').map(cell => cell.replace(/^"|"$/g, '').trim());
        
        if (cells.length < 6) return;
        
        const [objName, meterName, meterType, datetime, value, unit, consumption, forecast, gasZ, gasHeat] = cells;
        
        // Find or create object
        let obj = importedData.objects.find(o => o.name === objName);
        if (!obj) {
          obj = {
            id: `obj-${Date.now()}-${Math.random()}`,
            name: objName,
            meters: []
          };
          importedData.objects.push(obj);
        }
        
        // Find or create meter
        let meter = obj.meters.find(m => m.name === meterName);
        if (!meter) {
          const typeMap = {
            'Strom': 'electricity',
            'Gas': 'gas',
            'Wasser': 'water',
            'Wasser (kalt)': 'waterCold',
            'Wasser (warm)': 'waterWarm'
          };
          
          meter = {
            id: `meter-${Date.now()}-${Math.random()}`,
            name: meterName,
            type: typeMap[meterType] || 'electricity',
            readings: [],
            params: {}
          };
          
          if (meter.type === 'gas' && gasZ && gasHeat) {
            meter.params = {
              zNumber: parseFloat(gasZ.replace(',', '.')),
              heatingValue: parseFloat(gasHeat.replace(',', '.'))
            };
          }
          
          obj.meters.push(meter);
        }
        
        // Add reading
        const [datePart, timePart] = datetime.split(' ');
        const [day, month, year] = datePart.split('.');
        const timestamp = new Date(`${year}-${month}-${day}T${timePart}`).toISOString();
        
        const reading = {
          id: `reading-${Date.now()}-${Math.random()}`,
          timestamp,
          value: parseFloat(value.replace(',', '.'))
        };
        
        if (meter.type === 'gas' && gasZ && gasHeat) {
          reading.gasParams = {
            zNumber: parseFloat(gasZ.replace(',', '.')),
            heatingValue: parseFloat(gasHeat.replace(',', '.'))
          };
        }
        
        meter.readings.push(reading);
        importedData.stats.totalReadings++;
      });
      
      onSuccess(importedData);
    } catch (error) {
      console.error('CSV import error:', error);
      alert('Fehler beim Importieren der CSV-Datei. Bitte überprüfen Sie das Format.');
    }
  };
  
  reader.readAsText(file);
};

const getMeterTypeLabel = (type) => {
  const labels = {
    electricity: 'Strom',
    gas: 'Gas',
    water: 'Wasser',
    waterCold: 'Wasser (kalt)',
    waterWarm: 'Wasser (warm)'
  };
  return labels[type] || type;
};